import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Save, CheckCircle2 } from "lucide-react";

const SASB_SECTORS = [
  "Technology & Communications", "Financials", "Services", "Consumer Goods",
  "Healthcare", "Industrials", "Transportation", "Renewable Resources",
  "Extractives & Minerals Processing", "Infrastructure", "Resource Transformation", "Food & Beverage",
];

const FRAMEWORKS = [
  "IFRS S1", "IFRS S2", "GRI", "SASB", "TCFD", "GHG Protocol",
  "ISO 14001", "ISO 14064", "ISO/IEC 27001", "ISO 45001",
];

const BOUNDARIES = [
  { v: "operational_control", l: "Operational control" },
  { v: "financial_control", l: "Financial control" },
  { v: "equity_share", l: "Equity share" },
];

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function OrgSettings() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/org-profile").then(r => setForm(r.data));
  }, []);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleFw = (fw) => {
    const has = (form.frameworks || []).includes(fw);
    upd("frameworks", has ? form.frameworks.filter(x => x !== fw) : [...(form.frameworks||[]), fw]);
  };

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const { tenant_id, tenant_name, updated_at, updated_by, ...payload } = form;
      await api.put("/admin/org-profile", payload);
      setMsg("Saved.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!form) return <div className="p-6 text-ink-soft">Loading…</div>;

  return (
    <div className="p-6 space-y-5" data-testid="org-settings-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="section-label">Admin · Organisation</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1">{form.tenant_name}</h1>
          <p className="text-sm text-ink-soft">SASB-aligned accounting foundation for this tenant. These choices determine consolidation, reporting boundary and standards mapping.</p>
        </div>
        <button data-testid="org-save-button" disabled={busy} onClick={save} className="btn btn-primary"><Save size={14}/> Save profile</button>
      </div>

      {msg && <div className="text-sm text-status-success border border-emerald-200 bg-emerald-50 rounded-md px-3 py-2 flex items-center gap-2"><CheckCircle2 size={14}/> {msg}</div>}
      {err && <div className="text-sm text-status-error border border-red-200 bg-red-50 rounded-md px-3 py-2">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-5 space-y-3">
          <div className="section-label">Identity</div>
          {[
            ["legal_name", "Legal entity name"],
            ["common_name", "Common name"],
            ["headquarters", "Headquarters address"],
            ["country", "Country"],
            ["region", "Region"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="dlabel">{label}</label>
              <input data-testid={`org-${k}`} value={form[k] ?? ""} onChange={e=>upd(k, e.target.value)} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-brand"/>
            </div>
          ))}
        </div>

        <div className="card p-5 space-y-3">
          <div className="section-label">Industry classification (SASB SICS)</div>
          <div>
            <label className="dlabel">SASB sector</label>
            <select data-testid="org-sasb_sector" value={form.sasb_sector||""} onChange={e=>upd("sasb_sector", e.target.value)} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm">
              <option value="">— select —</option>
              {SASB_SECTORS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="dlabel">SASB industry (e.g. Software & IT Services / Professional & Commercial Services)</label>
            <input data-testid="org-sasb_industry" value={form.sasb_industry||""} onChange={e=>upd("sasb_industry", e.target.value)} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
          </div>
        </div>

        <div className="card p-5 space-y-3">
          <div className="section-label">Reporting boundary & fiscal</div>
          <div>
            <label className="dlabel">Reporting boundary (GHG Protocol)</label>
            <select data-testid="org-reporting_boundary" value={form.reporting_boundary||""} onChange={e=>upd("reporting_boundary", e.target.value)} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm">
              {BOUNDARIES.map(b => <option key={b.v} value={b.v}>{b.l}</option>)}
            </select>
          </div>
          <div>
            <label className="dlabel">Consolidation approach</label>
            <input data-testid="org-consolidation_approach" value={form.consolidation_approach||""} onChange={e=>upd("consolidation_approach", e.target.value)} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="dlabel">Fiscal year start (month)</label>
              <select data-testid="org-fy_month" value={form.fiscal_year_start_month||1} onChange={e=>upd("fiscal_year_start_month", Number(e.target.value))} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm">
                {MONTHS.map((m,i)=> <option key={m} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="dlabel">Reporting currency</label>
              <input data-testid="org-currency" value={form.reporting_currency||""} onChange={e=>upd("reporting_currency", e.target.value)} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="dlabel">Baseline year</label>
              <input data-testid="org-baseline_year" type="number" value={form.baseline_year||""} onChange={e=>upd("baseline_year", Number(e.target.value))} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
            </div>
            <div>
              <label className="dlabel">First reporting period</label>
              <input data-testid="org-first_period" value={form.first_reporting_period||""} onChange={e=>upd("first_reporting_period", e.target.value)} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
            </div>
          </div>
        </div>

        <div className="card p-5 space-y-3">
          <div className="section-label">Materiality & operations</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="dlabel">Headcount</label>
              <input data-testid="org-headcount" type="number" value={form.headcount||""} onChange={e=>upd("headcount", Number(e.target.value))} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
            </div>
            <div>
              <label className="dlabel">Annual revenue (USD)</label>
              <input data-testid="org-revenue" type="number" value={form.annual_revenue_usd||""} onChange={e=>upd("annual_revenue_usd", Number(e.target.value))} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
            </div>
          </div>
          <div>
            <label className="dlabel">Materiality assessment date</label>
            <input data-testid="org-materiality_date" type="date" value={form.materiality_assessment_date||""} onChange={e=>upd("materiality_assessment_date", e.target.value)} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="dlabel">Primary contact</label>
              <input data-testid="org-contact_name" placeholder="Name" value={form.primary_contact_name||""} onChange={e=>upd("primary_contact_name", e.target.value)} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
            </div>
            <div>
              <label className="dlabel">Email</label>
              <input data-testid="org-contact_email" placeholder="email" value={form.primary_contact_email||""} onChange={e=>upd("primary_contact_email", e.target.value)} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
            </div>
            <div>
              <label className="dlabel">Role</label>
              <input data-testid="org-contact_role" placeholder="Role" value={form.primary_contact_role||""} onChange={e=>upd("primary_contact_role", e.target.value)} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
            </div>
          </div>
        </div>

        <div className="card p-5 lg:col-span-2">
          <div className="section-label">Disclosure frameworks</div>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="org-frameworks">
            {FRAMEWORKS.map(fw => {
              const on = (form.frameworks||[]).includes(fw);
              return (
                <button
                  key={fw} type="button"
                  data-testid={`fw-${fw.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                  onClick={() => toggleFw(fw)}
                  className={"pill " + (on ? "pill-success" : "")}
                >{fw}</button>
              );
            })}
          </div>
          <div>
            <label className="dlabel mt-4 block">Notes</label>
            <textarea data-testid="org-notes" value={form.notes||""} onChange={e=>upd("notes", e.target.value)} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm h-20"/>
          </div>
        </div>
      </div>
    </div>
  );
}
